/**
 * YearActivitySummarySection
 *
 * 올해의 활동 요약 섹션
 * - 주간 단위 타임라인 시각화
 * - 원 크기=조회수, 투명도=참여율
 * - 에디터리얼 스타일
 */

import React from 'react';
import { Box, Stack, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Section } from '../../components/Container/Section';
import YearEndSectionTitle from './YearEndSectionTitle';
import WeeklyActivityChart from '../../components/YearEnd/WeeklyActivityChart';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * 통계 아이템 컴포넌트
 */
const StatItem = ({ label, value, suffix = '' }) => {
  const theme = useTheme();

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"source-han-serif-korean", "Noto Serif KR", serif',
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1.2,
        }}
      >
        {value}{suffix}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};


/**
 * 스켈레톤 로딩 컴포넌트
 */
const ActivitySkeleton = () => (
  <Box sx={{ py: 4 }}>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={200}
      sx={{ borderRadius: 1, mb: 2 }}
    />
    <Stack direction="row" spacing={4} justifyContent="center">
      <Skeleton variant="text" width={60} height={40} />
      <Skeleton variant="text" width={60} height={40} />
      <Skeleton variant="text" width={60} height={40} />
    </Stack>
  </Box>
);

export const YearActivitySummarySection = ({
  activitySummary,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useIsMobile('md');

  // 로딩 중
  if (isLoading) {
    return (
      <Section>
        <YearEndSectionTitle
          title={t('yearEnd.activity.title', { defaultValue: '올해의 활동' })}
        >
          <ActivitySkeleton />
        </YearEndSectionTitle>
      </Section>
    );
  }

  // 데이터 없음
  if (!activitySummary || !activitySummary.weeks) {
    return null;
  }

  const { stats } = activitySummary;

  return (
    <Section sx={{ py: { xs: 4, md: 6 } }}>
      <YearEndSectionTitle
        title={t('yearEnd.activity.title', { defaultValue: '올해의 활동' })}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          style={{ paddingTop: 24, paddingBottom: 32 }}
        >
          {/* 간단 통계 */}
          <Stack
            direction="row"
            spacing={isMobile ? 3 : 6}
            justifyContent="center"
            sx={{ mb: 4, pt: 2 }}
          >
            <StatItem
              label={t('yearEnd.activity.stats.weeksActive', { defaultValue: '활동한 주' })}
              value={stats?.totalWeeksActive || 0}
              suffix={t('yearEnd.activity.stats.weekSuffix', { defaultValue: '주' })}
            />
            <StatItem
              label={t('yearEnd.activity.stats.longestStreak', { defaultValue: '연속 활동' })}
              value={stats?.longestStreak || 0}
              suffix={t('yearEnd.activity.stats.weekSuffix', { defaultValue: '주' })}
            />
            <StatItem
              label={t('yearEnd.activity.stats.avgPerWeek', { defaultValue: '주당 평균' })}
              value={stats?.avgThreadsPerWeek || 0}
              suffix={t('yearEnd.activity.stats.threadSuffix', { defaultValue: '개' })}
            />
          </Stack>

          {/* 차트 */}
          <Box sx={{ py: 2 }}>
            <WeeklyActivityChart
              activitySummary={activitySummary}
            />
          </Box>

          {/* 하단 설명 */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.disabled',
              mt: 1,
            }}
          >
            {t('yearEnd.activity.description', {
              defaultValue: '원의 크기는 조회수, 진하기는 참여율을 나타냅니다',
            })}
          </Typography>
        </motion.div>
      </YearEndSectionTitle>
    </Section>
  );
};

export default YearActivitySummarySection;
